import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Where the capture flow currently is. Exactly one of the five camera modals is open per state;
 * `closed` renders nothing.
 *
 * `permissionDenied` and `cameraUnavailable` are kept apart because the user's next move differs:
 * the first is fixed by granting access, the second only by finding a device that has a working
 * camera — telling someone to check their permissions when the machine has no camera at all sends
 * them hunting through browser settings for a switch that isn't there.
 */
export type CameraCaptureState =
  | "closed"
  | "selectOption"
  | "camera"
  | "permissionDenied"
  | "cameraUnavailable"
  | "review";

/**
 * The confirmed image as the platform upload path receives it: raw binary, never base64 — the
 * upload endpoints take the bytes as a multipart part. The platform wraps the blob into whatever
 * its transport wants (web: `new File([blob], name, { type })` into `handleFileUploadChange`).
 */
export type CapturedImage = {
  blob: Blob;
  type: string;
  name: string;
};

export type UseCameraCaptureProps = {
  /**
   * The field's `allowCameraCaptureOnly` flag: skip the source-selection popup and open the camera
   * straight away; the browse option is never offered.
   */
  cameraOnly?: boolean;
  /** The field's `allowedFileTypes`; decides the mime type the photo is encoded as. */
  allowedFileTypes?: string[];
  /** Platform: open the existing file-browse UI (web clicks the field's hidden `<input type="file">`). */
  onBrowse: () => void;
  /** Platform: push the confirmed image into the upload path. */
  onImageReady: (image: CapturedImage) => void;
  /**
   * Platform: re-encode the photo with the review rotation applied. Defaults to the web canvas
   * implementation; mobile must inject its own (e.g. expo-image-manipulator) — the default touches
   * `document` / `Image`, which React Native does not have.
   */
  bakeRotation?: (image: Blob, rotation: number, type: string) => Promise<Blob>;
};

/** Formats `canvas.toBlob` can actually encode, in preference order. */
const CANVAS_CAPTURE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * The template config offers `image/jpg` as an allowed type, but the canvas can only encode the
 * canonical `image/jpeg` — the flow encodes with the canonical mime and declares the field's
 * spelling on the File, since `validateFileType` matches `file.type` against the field verbatim.
 */
const CAPTURE_TYPE_ALIASES: Record<string, string> = { "image/jpg": "image/jpeg" };

const toCanvasType = (type: string) => CAPTURE_TYPE_ALIASES[type] ?? type;

/**
 * `getUserMedia` rejection names that mean the device cannot hand over a camera at all — none
 * attached, one that the OS or another app is already holding, or one that can't satisfy the
 * requested constraints. Everything else (`NotAllowedError`, `SecurityError`, and anything
 * unrecognised) is treated as a permission problem, which is both the common case and the one
 * whose copy already covers "or switch devices".
 */
const CAMERA_UNAVAILABLE_ERRORS = [
  "NotFoundError",
  "DevicesNotFoundError",
  "NotReadableError",
  "TrackStartError",
  "OverconstrainedError",
  "ConstraintNotSatisfiedError",
];

const cameraFailureState = (error: unknown): CameraCaptureState =>
  CAMERA_UNAVAILABLE_ERRORS.includes((error as { name?: string } | null)?.name ?? "")
    ? "cameraUnavailable"
    : "permissionDenied";

/**
 * Whether the feed should be flipped horizontally, i.e. shown as a mirror rather than as the camera
 * sees it.
 *
 * Only front-facing cameras get this. Facing yourself, an unmirrored feed inverts every movement —
 * you reach right and the preview goes left — so a selfie view is universally mirrored; a rear
 * camera pointed at a wall is already showing the scene the way you see it, and flipping it would
 * be the disorienting one. `facingMode` goes unreported by most laptop webcams, which are
 * user-facing, so anything that isn't explicitly `environment` is treated as a selfie view.
 *
 * Whatever this decides applies to the saved photo too: `capturePhoto` bakes the same flip into the
 * canvas, so the file is the frame the user framed. The trade-off is deliberate — a mirrored shot
 * renders any signage or serial number in it backwards, which is a real cost on inspection
 * evidence, but a review step that flips the photo out from under the user is the worse surprise.
 */
const shouldMirrorPreview = (stream: MediaStream) =>
  stream.getVideoTracks()[0]?.getSettings().facingMode !== "environment";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/** Re-encodes the photo with the review rotation baked in, so the saved image matches the preview. */
const bakeRotationWeb = (image: Blob, rotation: number, type: string): Promise<Blob> =>
  new Promise((resolve) => {
    const sourceUrl = URL.createObjectURL(image);
    const finish = (result: Blob) => {
      URL.revokeObjectURL(sourceUrl);
      resolve(result);
    };
    const source = new Image();
    source.onload = () => {
      const swapSides = rotation % 180 !== 0;
      const canvas = document.createElement("canvas");
      canvas.width = swapSides ? source.height : source.width;
      canvas.height = swapSides ? source.width : source.height;
      const context = canvas.getContext("2d");
      if (!context) {
        finish(image);
        return;
      }
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate((rotation * Math.PI) / 180);
      context.drawImage(source, -source.width / 2, -source.height / 2);
      canvas.toBlob((blob) => finish(blob ?? image), type, 0.92);
    };
    source.onerror = () => finish(image);
    source.src = sourceUrl;
  });

/**
 * All logic of the "camera or browse" capture flow for `image` question fields:
 * source selection → permission handling → live capture → review (zoom / rotate / retake) → confirm.
 *
 * The photo stays binary end to end: `canvas.toBlob` on capture, an object URL for the review
 * preview, and a `Blob` handed to `onImageReady` — no base64 anywhere, matching the multipart
 * upload transport.
 *
 * Platform split (same seam as `useFieldsAttachment`): this hook owns the flow state and handlers;
 * the platform owns the capture primitive. Web drives `selectCamera` / `capturePhoto`, which use
 * `getUserMedia` + `<video>` + canvas. Mobile should skip those and call `handleCapturedImage` with
 * the Blob its own camera (expo-camera / expo-image-picker) produced — the shared review /
 * confirm flow takes over from there. `onBrowse` / `onImageReady` are injected per platform.
 */
const useCameraCapture = ({
  cameraOnly = false,
  allowedFileTypes = [],
  onBrowse,
  onImageReady,
  bakeRotation = bakeRotationWeb,
}: UseCameraCaptureProps) => {
  const [captureState, setCaptureState] = useState<CameraCaptureState>("closed");
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  /** Object URL over `capturedBlob`, for the review `<img>`. Revoked whenever the photo goes away. */
  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  /** Live-preview mirroring, decided per stream — see `shouldMirrorPreview`. */
  const [isPreviewMirrored, setIsPreviewMirrored] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  // Stales in-flight getUserMedia calls: bumped whenever the flow moves on (close, browse, a newer
  // start), so a late-resolving stream is stopped instead of committed.
  const cameraRequestIdRef = useRef(0);
  const confirmingRef = useRef(false);

  // The type declared on the File must match the field's `allowedFileTypes` verbatim
  // (`validateFileType`), while the canvas only encodes canonical mimes — so track them apart.
  const declaredCaptureType =
    CANVAS_CAPTURE_TYPES.map((canvasType) =>
      allowedFileTypes.find((allowed) => toCanvasType(allowed) === canvasType),
    ).find(Boolean) ?? "image/jpeg";
  const encodeCaptureType = toCanvasType(declaredCaptureType);

  const stopCameraStream = useCallback(() => {
    cameraRequestIdRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const releasePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopCameraStream();
      releasePreviewUrl();
    },
    [stopCameraStream, releasePreviewUrl],
  );

  const clearCapturedImage = () => {
    releasePreviewUrl();
    setCapturedBlob(null);
    setCapturedImageUrl(null);
  };

  /**
   * Callback ref for the live `<video>`: the element only mounts once the camera modal opens, so
   * the stream is attached here rather than when `getUserMedia` resolves.
   */
  const attachVideoElement = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    // Assigning srcObject is enough — the element renders with `autoPlay muted`, so playback
    // starts as soon as the stream attaches.
    if (element && streamRef.current && element.srcObject !== streamRef.current) {
      element.srcObject = streamRef.current;
    }
  }, []);

  const startCamera = async () => {
    stopCameraStream();
    // No capture API to ask in the first place — an old browser, or a page served over plain
    // http, where `mediaDevices` is simply absent. Nothing to grant, so it's the unavailable modal.
    if (!navigator.mediaDevices?.getUserMedia) {
      setCaptureState("cameraUnavailable");
      return;
    }
    const requestId = cameraRequestIdRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      // The flow moved on while the permission prompt was open (closed, browsed, or a newer
      // start): discard this stream instead of committing it, or the camera stays on orphaned.
      if (requestId !== cameraRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      setIsPreviewMirrored(shouldMirrorPreview(stream));
      setCaptureState("camera");
    } catch (error) {
      // Same staleness guard as the success path: a failure that belongs to a superseded request
      // must not reopen a modal over whatever the flow moved on to.
      if (requestId !== cameraRequestIdRef.current) return;
      // Surfacing the right dead-end modal is how this error is handled — there is no caller left
      // to reject to, the flow ends here with the user choosing retry or cancel.
      setCaptureState(cameraFailureState(error));
    }
  };

  const resetAdjustments = () => {
    setZoom(1);
    setRotation(0);
  };

  /** Entry point for the field's upload button. */
  const openImageCapture = () => {
    clearCapturedImage();
    resetAdjustments();
    if (cameraOnly) {
      startCamera();
    } else {
      setCaptureState("selectOption");
    }
  };

  const selectBrowse = () => {
    // Also stales any getUserMedia still pending, so it can't reopen the camera modal later.
    stopCameraStream();
    setCaptureState("closed");
    onBrowse();
  };

  const selectCamera = () => startCamera();

  const retryCamera = () => startCamera();

  /**
   * Value-based hand-off into the review step — mobile calls this directly with the Blob its
   * native camera produced.
   */
  const handleCapturedImage = (image: Blob) => {
    stopCameraStream();
    releasePreviewUrl();
    const previewUrl = URL.createObjectURL(image);
    previewUrlRef.current = previewUrl;
    setCapturedBlob(image);
    setCapturedImageUrl(previewUrl);
    resetAdjustments();
    setCaptureState("review");
  };

  /**
   * Web shutter: grabs the current video frame onto a canvas, straight to binary.
   *
   * The preview's mirroring is baked in rather than left as the CSS-only flip it is on screen, so
   * the photo is the frame the user actually composed — reviewing a shot that comes back flipped
   * from the one just framed reads as the app having got it wrong.
   */
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    if (isPreviewMirrored) {
      // Flip about the canvas's vertical centre line: `scale(-1, 1)` alone mirrors across x=0 and
      // would put the frame entirely off-canvas, so shift the origin a full width first.
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) handleCapturedImage(blob);
      },
      encodeCaptureType,
      0.92,
    );
  };

  const retakePhoto = () => {
    clearCapturedImage();
    startCamera();
  };

  const zoomIn = () => setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  const zoomOut = () => setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  const rotateLeft = () => setRotation((prev) => prev - 90);
  const rotateRight = () => setRotation((prev) => prev + 90);

  const closeCapture = () => {
    stopCameraStream();
    clearCapturedImage();
    setCaptureState("closed");
  };

  /** "Use Image": bakes the review rotation into the photo and hands it to the upload path. */
  const confirmImage = async () => {
    // The ref (not state) makes this single-shot across the rotation bake's await — a fast
    // double-click would otherwise upload the photo twice.
    if (!capturedBlob || confirmingRef.current) return;
    confirmingRef.current = true;
    try {
      const normalizedRotation = ((rotation % 360) + 360) % 360;
      const blob =
        normalizedRotation === 0
          ? capturedBlob
          : await bakeRotation(capturedBlob, normalizedRotation, encodeCaptureType);
      // Same name convention as mobile's handleCameraPress, so entries read identically on both platforms.
      onImageReady({
        blob,
        type: declaredCaptureType,
        name: `Capture Image ${new Date()}.${declaredCaptureType.split("/")[1]}`,
      });
      closeCapture();
    } finally {
      confirmingRef.current = false;
    }
  };

  return {
    captureState,
    capturedImageUrl,
    zoom,
    zoomPercent: Math.round(zoom * 100),
    rotation,
    isPreviewMirrored,
    attachVideoElement,
    openImageCapture,
    selectBrowse,
    selectCamera,
    retryCamera,
    capturePhoto,
    handleCapturedImage,
    retakePhoto,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    resetAdjustments,
    confirmImage,
    closeCapture,
  };
};

export type CameraCaptureHandle = ReturnType<typeof useCameraCapture>;

export default useCameraCapture;
